// path: src/Components/DifficultyDialog.js

import React, { useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";

import {
    CHALLENGING_START_LEVEL,
    HARD_START_LEVEL,
    isChallengingUnlocked,
    isHardUnlocked,
    isShyCorgisUnlocked,
    isTwistedModeUnlocked,
} from "../utils/progression";

export default function DifficultyDialog({ open, onClose, onPick }) {
    const [selectedDifficulty, setSelectedDifficulty] = useState("easy");
    const [twistedMode, setTwistedMode] = useState(false);
    const [shyCorgisMode, setShyCorgisMode] = useState(false);
    const [hardcoreMode, setHardcoreMode] = useState(false);

    const challengingLocked = !isChallengingUnlocked();
    const hardLocked = !isHardUnlocked();
    const twistedLocked = !isTwistedModeUnlocked();
    const shyCorgisLocked = !isShyCorgisUnlocked();

    const blitzSelected = selectedDifficulty === "blitz";

    useEffect(() => {
        if (!open) {
            return;
        }

        setSelectedDifficulty("easy");

        if (twistedLocked) {
            setTwistedMode(false);
        }

        if (shyCorgisLocked) {
            setShyCorgisMode(false);
        }

        setHardcoreMode(false);
    }, [open, twistedLocked, shyCorgisLocked]);

    const getModifierOptions = () => ({
        twistedMode: !twistedLocked && twistedMode,
        shyCorgisMode: !shyCorgisLocked && shyCorgisMode,
    });

    const handleSelectDifficulty = (mode) => {
        const wasBlitzSelected = selectedDifficulty === "blitz";
        const isBlitzSelected = mode === "blitz";

        setSelectedDifficulty(mode);

        if (isBlitzSelected) {
            setHardcoreMode(true);
            return;
        }

        if (wasBlitzSelected) {
            setHardcoreMode(false);
        }
    };

    const handleStartRun = () => {
        if (selectedDifficulty === "blitz") {
            onPick("blitz", {
                ...getModifierOptions(),
                hardcoreMode: true,
                blitzMode: true,
            });
            return;
        }

        onPick(selectedDifficulty, {
            ...getModifierOptions(),
            hardcoreMode,
            blitzMode: false,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} aria-labelledby="difficulty-title">
            <DialogTitle id="difficulty-title">Choose difficulty</DialogTitle>

            <DialogContent dividers>
                <Typography gutterBottom>
                    Select a difficulty, choose any modifiers, then press Start Run.
                </Typography>

                <div className="difficultyPicker" role="group" aria-label="Difficulty">
                    <button
                        type="button"
                        className={`difficultyPickerButton ${
                            selectedDifficulty === "easy"
                                ? "difficultyPickerButton--selected"
                                : ""
                        }`}
                        onClick={() => handleSelectDifficulty("easy")}
                    >
                        <strong>Easy</strong>
                        <span>Starts at Level 1.</span>
                    </button>

                    <button
                        type="button"
                        className={`difficultyPickerButton ${
                            selectedDifficulty === "challenging"
                                ? "difficultyPickerButton--selected"
                                : ""
                        }`}
                        onClick={() => handleSelectDifficulty("challenging")}
                        disabled={challengingLocked}
                        title={
                            challengingLocked
                                ? "Reach Level 4 to unlock Challenging"
                                : ""
                        }
                    >
                        <strong>Challenging</strong>
                        <span>
                            Starts at Level {CHALLENGING_START_LEVEL}, the first 3×3 level.
                        </span>
                    </button>

                    <button
                        type="button"
                        className={`difficultyPickerButton ${
                            selectedDifficulty === "hard"
                                ? "difficultyPickerButton--selected"
                                : ""
                        }`}
                        onClick={() => handleSelectDifficulty("hard")}
                        disabled={hardLocked}
                        title={hardLocked ? "Reach Level 7 to unlock Insane" : ""}
                    >
                        <strong>Insane</strong>
                        <span>
                            Starts at Level {HARD_START_LEVEL}, the first 4×4 level.
                        </span>
                    </button>

                    <button
                        type="button"
                        className={`difficultyPickerButton ${
                            selectedDifficulty === "blitz"
                                ? "difficultyPickerButton--selected"
                                : ""
                        }`}
                        onClick={() => handleSelectDifficulty("blitz")}
                    >
                        <strong>Blitz</strong>
                        <span>
                            Starts at Level 1 with 2 minutes. Hardcore is always active.
                        </span>
                    </button>
                </div>

                {(challengingLocked || hardLocked) && (
                    <Typography style={{ marginTop: 12 }}>
                        Locked difficulties unlock once you reach those levels in a normal run.
                    </Typography>
                )}

                <div className="twistedModeOption">
                    <label
                        className={`twistedModeOption__label ${
                            twistedLocked ? "twistedModeOption__label--locked" : ""
                        }`}
                    >
                        <input
                            type="checkbox"
                            checked={twistedMode}
                            disabled={twistedLocked}
                            onChange={(event) => setTwistedMode(event.target.checked)}
                        />

                        <span>
                            <strong>Twisted Mode</strong>
                            <span className="twistedModeOption__description">
                                Cards randomly rotate 0°, 90°, 180°, or 270° after each shuffle.
                            </span>
                        </span>
                    </label>

                    {twistedLocked && (
                        <Typography style={{ marginTop: 8, fontSize: 13 }}>
                            Complete the base game to unlock Twisted Mode.
                        </Typography>
                    )}
                </div>

                <div className="twistedModeOption">
                    <label
                        className={`twistedModeOption__label ${
                            shyCorgisLocked ? "twistedModeOption__label--locked" : ""
                        }`}
                    >
                        <input
                            type="checkbox"
                            checked={shyCorgisMode}
                            disabled={shyCorgisLocked}
                            onChange={(event) => setShyCorgisMode(event.target.checked)}
                        />

                        <span>
                            <strong>Shy Corgis</strong>
                            <span className="twistedModeOption__description">
                                Cards randomly flip face-down for a few seconds, then flip back over.
                            </span>
                        </span>
                    </label>

                    {shyCorgisLocked && (
                        <Typography style={{ marginTop: 8, fontSize: 13 }}>
                            Complete the base game to unlock Shy Corgis.
                        </Typography>
                    )}
                </div>

                <div className="twistedModeOption">
                    <label className="twistedModeOption__label">
                        <input
                            type="checkbox"
                            checked={blitzSelected ? true : hardcoreMode}
                            disabled={blitzSelected}
                            onChange={(event) => setHardcoreMode(event.target.checked)}
                        />

                        <span>
                            <strong>Hardcore Mode</strong>
                            <span className="twistedModeOption__description">
                                Wrong cards immediately end the run. Hearts cannot be used.
                                {blitzSelected && " Always active for Blitz."}
                            </span>
                        </span>
                    </label>
                </div>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>

                <Button
                    onClick={handleStartRun}
                    variant="contained"
                    color="primary"
                >
                    Start Run
                </Button>
            </DialogActions>
        </Dialog>
    );
}